import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { catchError, distinctUntilChanged, EMPTY, map, switchMap } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { DividerModule } from '@openng/optimus-ui/divider';
import { TableModule } from '@openng/optimus-ui/table';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { TagModule } from '@openng/optimus-ui/tag';
import { SessionTimelineComponent } from '../timeline/session-timeline.component';
import {
  LiveSessionDetail,
  MessageContent,
  PostSessionTranscriptionStatus,
  PracticeSessionDetail,
  SessionDetailResponse,
  SessionType,
  TimestampedSentence,
} from '../../../models/session.types';
import { SessionsService } from '../../../services/sessions.service';
import { ErrorBannerComponent } from '../../shared/error-banner.component';

@Component({
  selector: 'app-session-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    CardModule,
    DividerModule,
    TableModule,
    TabsModule,
    TagModule,
    SessionTimelineComponent,
    ErrorBannerComponent,
  ],
  templateUrl: './session-detail.component.html',
  styleUrl: './session-detail.component.scss',
})
export class SessionDetailComponent {
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private sessionsService = inject(SessionsService);

  protected detail = signal<SessionDetailResponse | null>(null);
  protected loading = signal(true);
  protected errorMessage = signal<string | null>(null);

  protected practiceDetail = computed(() => {
    const detail = this.detail();
    return detail?.type === 'PRACTICE' ? (detail.data as PracticeSessionDetail) : undefined;
  });

  protected liveDetail = computed(() => {
    const detail = this.detail();
    return detail?.type === 'LIVE' ? (detail.data as LiveSessionDetail) : undefined;
  });

  protected chatMessages = computed(() => this.detail()?.data.messages || []);

  protected liveTranscript = computed(() => this.liveDetail()?.transcription || []);

  protected postSession = computed(() => this.liveDetail()?.postSessionTranscription);

  protected postSessionTranscript = computed(() => this.postSession()?.transcription || []);

  protected postSessionStatus = computed<PostSessionTranscriptionStatus | undefined>(
    () => this.postSession()?.status,
  );

  protected llmLogs = computed(() => this.detail()?.data.llmLogs || []);

  protected llmCallCounts = computed(() => {
    const counts = this.liveDetail()?.llmCallCounts;
    if (!counts) return [];

    return Object.entries(counts).map(([key, value]) => ({
      label: key
        .replace(/LlmCallCount$/, '')
        .replace(/([A-Z])/g, ' $1')
        .trim(),
      count: value as number,
    }));
  });

  protected llmTraceSummary = computed(() => {
    const logs = this.llmLogs();
    return {
      totalCalls: logs.length,
      totalCost: logs.reduce((acc, log) => acc + (Number(log.totalCost) || 0), 0),
    };
  });

  protected llmTracePerModelSummary = computed(() => {
    const byModel = this.llmLogs().reduce(
      (acc, log) => {
        const model = log.model || 'Unknown';

        if (!acc[model]) {
          acc[model] = { model, calls: 0, cost: 0 };
        }

        acc[model].calls += 1;
        acc[model].cost += Number(log.totalCost) || 0;

        return acc;
      },
      {} as Record<string, { model: string; calls: number; cost: number }>,
    );

    return Object.values(byModel).sort((a, b) => b.calls - a.calls);
  });

  protected llmTraceTotalCost = computed(() =>
    this.llmTracePerModelSummary().reduce((acc, model) => acc + model.cost, 0),
  );

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => ({
          sessionId: params.get('sessionId'),
          type: this.parseSessionType(params.get('type')),
        })),
        distinctUntilChanged(
          (prev, curr) => prev.sessionId === curr.sessionId && prev.type === curr.type,
        ),
        switchMap(({ sessionId, type }) => {
          if (!sessionId || !type) {
            this.loading.set(false);
            this.detail.set(null);
            this.errorMessage.set('Invalid session URL.');
            return EMPTY;
          }

          this.loading.set(true);
          this.detail.set(null);
          this.errorMessage.set(null);

          return this.sessionsService.getSessionDetail(sessionId, type).pipe(
            catchError((error: { error?: { message?: string }; message?: string }) => {
              this.loading.set(false);
              this.errorMessage.set(
                error.error?.message || error.message || 'Failed to load session details.',
              );
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        this.detail.set(detail);
        this.loading.set(false);
      });
  }

  protected formatMessageContent(content: MessageContent | string): string {
    if (typeof content === 'string') return content;
    if (content && content.text) return content.text;
    return JSON.stringify(content);
  }

  protected getStatusSeverity(
    status: string,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'ACTIVE':
        return 'info';
      case 'ARCHIVED':
        return 'secondary';
      default:
        return 'contrast';
    }
  }

  protected getDifficulty(config: Record<string, unknown>): string {
    return (config['difficulty'] as string) || 'N/A';
  }

  protected downloadScenario(): void {
    const detail = this.detail();

    if (!detail || detail.type !== 'LIVE') return;

    this.sessionsService.exportScenario(detail.data.id).subscribe({
      next: (scenario) => {
        const json = JSON.stringify(scenario, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const name = (scenario['name'] as string) || detail.data.id;

        link.href = url;
        link.download = `${name
          .replace(/[^a-zA-Z0-9-_ ]/g, '')
          .replace(/\s+/g, '-')
          .toLowerCase()}.scenario.json`;
        link.click();

        URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.errorMessage.set(error.message || 'Failed to export scenario.');
      },
    });
  }

  protected getPostSessionSeverity(
    status: PostSessionTranscriptionStatus | undefined,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | undefined {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
      case 'pending':
        return 'info';
      case 'failed':
        return 'danger';
      case 'skipped':
        return 'secondary';
      default:
        return undefined;
    }
  }

  protected downloadTranscript(source: 'live' | 'post'): void {
    const live = this.liveDetail();

    if (!live) return;

    const sentences = source === 'live' ? this.liveTranscript() : this.postSessionTranscript();
    const text =
      source === 'post' && !sentences.length && this.postSession()?.transcript
        ? (this.postSession()?.transcript as string)
        : this.transcriptToText(sentences);

    if (!text.trim()) return;

    const suffix = source === 'live' ? 'live-transcript' : 'post-session-transcript';
    this.downloadBlob(text, 'text/plain', this.transcriptFileName(live, suffix, 'txt'));
  }

  protected downloadPostSessionJson(): void {
    const live = this.liveDetail();
    const payload = this.postSession();

    if (!live || !payload) return;

    this.downloadBlob(
      JSON.stringify(payload, null, 2),
      'application/json',
      this.transcriptFileName(live, 'post-session-transcript', 'json'),
    );
  }

  private transcriptToText(sentences: TimestampedSentence[]): string {
    return sentences.map((item) => `${item.speaker}: ${item.text}`).join('\n');
  }

  private transcriptFileName(live: LiveSessionDetail, suffix: string, ext: string): string {
    const base = (live.meetingTitle || live.id)
      .replace(/[^a-zA-Z0-9-_ ]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase();
    return `${base}.${suffix}.${ext}`;
  }

  private downloadBlob(content: string, mimeType: string, fileName: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  }

  private parseSessionType(type: string | null): SessionType | null {
    const normalizedType = type?.toUpperCase();

    if (normalizedType === 'LIVE' || normalizedType === 'PRACTICE') {
      return normalizedType;
    }

    return null;
  }
}
